import React, { useState } from "react";
import styled, { css } from "styled-components";
import AlertsModals from "security-app/components/SecurityEvents/Alerts/AlertsModals";
import { ModalProvider, useModalContext } from "common/contexts";
import { Spinner } from "components/common";
import EventActions from "security-app/components/SecurityEvents/ReplaySearch/EventActions";
import { useGetSecurityEvent } from "security-app/hooks/useSecurityEventsAPI";
import { SegmentedControl, Button } from "components/bootstrap";
import EventDetailsDefinitionList from "components/events/events/EventDetailsDefinitionList";
import RemediationSteps from "components/events/ReplaySearchSidebar/RemediationSteps";
import columnRenderers from "security-app/components/SecurityEvents/Alerts/ColumnRenderers";
import { securityEventDetailsAttributes } from "security-app/components/SecurityEvents/Alerts/Constants";
import useEventsAdditionalData from "components/events/ReplaySearchSidebar/hooks/useEventsAdditionalData";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import EventNotes from "security-app/components/SecurityEvents/ReplaySearch/EventNotes";
import SetStatusControl from "security-app/components/SecurityEvents/Alerts/EventDetails/SetStatusControl";
import ReplaySearchContext from "components/event-definitions/replay-search/ReplaySearchContext";
import useFeature from "hooks/useFeature";
import { eventTypeAttribute } from "components/events/events/ColumnRenderers";
import { WordBreakRenderer } from "components/events/ReplaySearchSidebar/GeneralEventSideBar";
import OwnerLabel from "security-app/components/SecurityEvents/Alerts/EventDetails/OwnerLabel";
import useCanEditSecurityEvent from "security-app/components/SecurityEvents/Alerts/hooks/useCanEditSecurityEvent";
import EventDefinitionInfoList from "components/event-definitions/replay-search/EventDefinitionInfoList";
const sidebarAttributesOrder = [
  "priority",
  "scores",
  "timestamp",
  "status",
  "owner",
  "event_actions",
  "associated_investigations",
  "associated_assets",
  "event_definition_id",
  "event_definition_type",
  "key",
  "group_by_fields",
  "event_id",
  "fields"
];
const sidebarAttributesList = [
  ...securityEventDetailsAttributes,
  {
    id: "owner",
    title: "Owner",
    type: "STRING"
  },
  {
    id: "event_actions",
    title: ""
  }
].sort((a, b) => sidebarAttributesOrder.indexOf(a.id) - sidebarAttributesOrder.indexOf(b.id));
const legacyAttributesList = [
  {
    id: "timestamp",
    title: "Timestamp",
    type: "DATE"
  },
  {
    id: "alert",
    title: "Type"
  },
  {
    id: "message",
    type: "STRING",
    title: "Message"
  },
  {
    id: "status",
    type: "STRING",
    title: "Status"
  },
  {
    id: "owner",
    type: "STRING",
    title: "Owner"
  }
];
const legacyAttributesRenderers = {
  alert: eventTypeAttribute,
  message: { renderCell: WordBreakRenderer }
};
const StatusAction = ({
  securityEvent,
  canManageSecurityEvents
}) => {
  const { setModal, setEntity } = useModalContext();
  const onStatusChange = (newStatus) => {
    if (newStatus !== securityEvent.status) {
      setEntity({
        securityEvent,
        newStatus
      });
      setModal("CONFIRM-STATUS-CHANGE");
    }
  };
  return /* @__PURE__ */ React.createElement(
    SetStatusControl,
    {
      onStatusChange,
      value: securityEvent.status,
      disabled: !canManageSecurityEvents,
      label: null,
      width: null,
      size: "small"
    }
  );
};
const Container = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.sm};
    flex-direction: column;
  `
);
export const Tab = styled.div(
  ({ theme }) => css`
    padding: ${theme.spacings.xs} 0;
  `
);
const EventDetails = ({
  securityEvent,
  meta,
  permissions,
  canManageSecurityEvents,
  isRightSidebarEnabled,
  onNavigateToDefinition = void 0
}) => {
  var _a;
  const { event: generalEvent, ...rest } = securityEvent;
  const eventEntity = { ...rest, ...generalEvent };
  const attributesRenderers = {
    ...isRightSidebarEnabled ? columnRenderers.attributes : legacyAttributesRenderers,
    event_id: {
      renderCell: (eventId) => /* @__PURE__ */ React.createElement("div", null, eventId)
    },
    event_definition_id: {
      renderCell: (eventDefinitionId) => {
        var _a2, _b, _c;
        const canRead = isPermitted(permissions, `eventdefinitions:read:${eventDefinitionId}`);
        const title = (_c = (_b = (_a2 = meta == null ? void 0 : meta.context) == null ? void 0 : _a2.event_definitions) == null ? void 0 : _b[eventDefinitionId]) == null ? void 0 : _c.title;
        if (!canRead || !title) return /* @__PURE__ */ React.createElement("i", null, eventDefinitionId);
        if (!onNavigateToDefinition) return /* @__PURE__ */ React.createElement("span", null, title);
        return /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", onClick: onNavigateToDefinition }, title);
      }
    },
    event_actions: {
      renderCell: () => canManageSecurityEvents ? /* @__PURE__ */ React.createElement(EventActions, { securityEvent }) : null
    },
    owner: {
      renderCell: () => /* @__PURE__ */ React.createElement(OwnerLabel, { securityEvent })
    },
    status: {
      renderCell: () => /* @__PURE__ */ React.createElement(StatusAction, { securityEvent, canManageSecurityEvents })
    }
  };
  return /* @__PURE__ */ React.createElement(Container, null, ((_a = securityEvent.event) == null ? void 0 : _a.message) && /* @__PURE__ */ React.createElement("h3", null, securityEvent.event.message), /* @__PURE__ */ React.createElement(
    EventDetailsDefinitionList,
    {
      event: isRightSidebarEnabled ? eventEntity : securityEvent == null ? void 0 : securityEvent.event,
      meta,
      attributesList: isRightSidebarEnabled ? sidebarAttributesList : legacyAttributesList,
      attributesRenderers
    }
  ));
};
const EventDefinitionDetails = ({ definitionId, type }) => /* @__PURE__ */ React.createElement(ReplaySearchContext.Provider, { value: { alertId: void 0, definitionId, type } }, /* @__PURE__ */ React.createElement(EventDefinitionInfoList, null));
const EventReplaySideBarDetails = ({ alertId, definitionId }) => {
  var _a, _b;
  const { permissions } = useCurrentUser();
  const [selectedSegment, setSelectedSegment] = useState("overview");
  const isRightSidebarEnabled = useFeature("replay_search_right_sidebar");
  const canManageSecurityEvents = useCanEditSecurityEvent(alertId);
  const { securityEvent, loadingSecurityEvents } = useGetSecurityEvent(alertId);
  const resolvedDefinitionId = definitionId != null ? definitionId : (_a = securityEvent == null ? void 0 : securityEvent.event) == null ? void 0 : _a.event_definition_id;
  const { meta, eventDefinitionEventProcedureId, isLoadingEventDefinition } = useEventsAdditionalData({
    eventData: securityEvent == null ? void 0 : securityEvent.event,
    definitionId: resolvedDefinitionId
  });
  const eventType = ((_b = securityEvent == null ? void 0 : securityEvent.event) == null ? void 0 : _b.alert) ? "alert" : "event";
  const segments = [
    { value: "overview", label: "Overview" },
    { value: "procedures", label: "Procedures" },
    ...alertId ? [{ value: "notes", label: "Notes" }] : [],
    ...resolvedDefinitionId ? [{ value: "definition", label: "Definition" }] : []
  ];
  if (alertId && (loadingSecurityEvents || !securityEvent) || isLoadingEventDefinition) return /* @__PURE__ */ React.createElement(Spinner, null);
  return /* @__PURE__ */ React.createElement(ModalProvider, null, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SegmentedControl, { data: segments, value: selectedSegment, onChange: setSelectedSegment }), selectedSegment === "overview" && /* @__PURE__ */ React.createElement(Tab, null, securityEvent && /* @__PURE__ */ React.createElement(
    EventDetails,
    {
      securityEvent,
      meta,
      permissions,
      canManageSecurityEvents,
      isRightSidebarEnabled,
      onNavigateToDefinition: resolvedDefinitionId ? () => setSelectedSegment("definition") : void 0
    }
  )), selectedSegment === "procedures" && /* @__PURE__ */ React.createElement(Tab, null, /* @__PURE__ */ React.createElement(
    RemediationSteps,
    {
      event: securityEvent == null ? void 0 : securityEvent.event,
      meta,
      eventDefinitionEventProcedureId
    }
  )), selectedSegment === "notes" && /* @__PURE__ */ React.createElement(Tab, null, securityEvent ? /* @__PURE__ */ React.createElement(EventNotes, { editable: canManageSecurityEvents, securityEvent }) : /* @__PURE__ */ React.createElement("em", null, "No event data available")), selectedSegment === "definition" && /* @__PURE__ */ React.createElement(Tab, null, /* @__PURE__ */ React.createElement(EventDefinitionDetails, { definitionId: resolvedDefinitionId, type: eventType }))), /* @__PURE__ */ React.createElement(AlertsModals, null));
};
export default EventReplaySideBarDetails;
